import { widgetApi } from "../services/api";

/**
 * Mints a real fund-request magic link from the composed "request" —
 * `POST /widget/links/:linkId/fund-request-link`.
 *
 * The request widget calls this at confirm time: the requester has composed a
 * route, amount and payout method, and wants a link to share with whoever will
 * pay them. The backend resolves the requester's identity (minimum name +
 * destination country) server-side from their User record — the widget never
 * sends it — maps the composed payout into the fund-request contract, and
 * returns the shareable URL.
 *
 * Auth is the magic link: the `x-link-id` header is injected by the shared
 * `widgetApi` interceptor; `partnerId` is derived server-side from the link.
 *
 * Requester identity (minimum name + destination country) is resolved
 * server-side from the User record — flow-request does not run interactive CIP
 * before mint.
 */

export type FundRequestPayoutMethod =
  | "BANK_ACCOUNT"
  | "CASH_PICKUP"
  | "PUSH_TO_CARD"
  | "MOBILE_WALLET"
  | "HOME_DELIVERY";

/**
 * Smart-native payout details, flattened for the wire. Only the fields relevant
 * to `payoutMethod` are populated; the backend reads the rest as undefined.
 *
 * Client-only (never POST to mint — stripped in `payoutForMint`):
 * `source`, `cvsValidationId`, `numberToken`, `validatedFor`.
 */
export interface FundRequestPayoutInput {
  /**
   * Client-only: `saved` = registered instrument; `new` = pre-validated.
   * Not part of the mint DTO — strip before POST.
   */
  source?: "new" | "saved";
  // BANK_ACCOUNT
  bankId?: string;
  accountNumber?: string;
  // CASH_PICKUP / HOME_DELIVERY
  stateCode?: string;
  payerId?: string;
  // PUSH_TO_CARD
  cardPan?: string;
  cardExpiry?: string;
  cardholderName?: string;
  paymentMethodId?: string;
  brand?: string;
  last4?: string;
  /** Client-only CVS dry-run validationId — strip before POST. */
  cvsValidationId?: string;
  /** Client-only VGS PAN alias — strip before POST. */
  numberToken?: string;
  /**
   * Client-only: corridor countries the destination card was pre-validated for.
   * Cleared / re-validated when the transfer route countries change. Strip before POST.
   */
  validatedFor?: {
    sourceCountry: string;
    destinationCountry: string;
  };
  // MOBILE_WALLET
  carrierId?: string;
  phone?: string;
  // HOME_DELIVERY
  address?: string;
}

export interface GenerateFundRequestLinkInput {
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
  /** Amount the requester wants to receive, in the destination currency. */
  amount: number;
  payoutMethod: FundRequestPayoutMethod;
  payout: FundRequestPayoutInput;
  /** SKU of the pre-quote option the requester selected, when available. */
  capabilityKey?: string;
  /** Disambiguation follow-up. Omitted on the first mint. */
  dateOfBirth?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zip?: string;
    countryISO2?: string;
  };
}

export interface GenerateFundRequestLinkResult {
  /** Shareable fund-request URL handed to the payer. */
  url: string;
  linkId: string;
  expiresAt: string;
  /** Id of the money request (solicitud) this link fulfills. */
  requestId: string;
}

export type FundRequestMintOutcome =
  | ({ kind: "created" } & GenerateFundRequestLinkResult)
  | {
      kind: "disambiguate";
      requiredFields: Array<"dateOfBirth" | "address">;
    }
  | { kind: "review" };

interface GenerateFundRequestLinkApiResponse {
  success: boolean;
  data: GenerateFundRequestLinkResult & {
    status?: string;
    actionRequired?: {
      type?: string;
      requiredFields?: Array<"dateOfBirth" | "address">;
    };
  };
}

export const generateFundRequestLink = async (
  linkId: string,
  input: GenerateFundRequestLinkInput
): Promise<FundRequestMintOutcome> => {
  const { data } = await widgetApi.post<GenerateFundRequestLinkApiResponse>(
    `/widget/links/${linkId}/fund-request-link`,
    input
  );
  const body = data.data;
  if (body?.url && body.linkId && body.expiresAt && body.requestId) {
    return {
      kind: "created",
      url: body.url,
      linkId: body.linkId,
      expiresAt: body.expiresAt,
      requestId: body.requestId,
    };
  }
  if (body?.status === "in_review") return { kind: "review" };
  return {
    kind: "disambiguate",
    requiredFields: body?.actionRequired?.requiredFields?.length
      ? body.actionRequired.requiredFields
      : ["dateOfBirth", "address"],
  };
};
